/**
 * PILLAR-SPOKE CONTENT ARCHITECTURE
 *
 * This file defines the hub-and-spoke content strategy for SEO and AEO.
 * Each pillar is a core topic cluster that the site aims to own.
 * Spokes are individual blog posts that link back to the pillar and cross-link to each other.
 *
 * Strategy:
 * - Pillar pages are dedicated service landing pages (e.g., /services/gemini-agentic-ai)
 * - Each spoke post links back to its pillar and to 2-3 related spokes
 * - Tags map directly to pillar topics for consistent taxonomy
 * - FAQ schema on every spoke targets long-tail AEO queries
 * - External references (authoritative sources) boost E-E-A-T
 */

export interface PillarTopic {
  id: string;
  name: string;
  slug: string; // URL path for the pillar (service section or dedicated page)
  description: string;
  primaryKeyword: string;
  secondaryKeywords: string[];
  relatedTags: string[];
  spokeSlugs: string[]; // Blog post slugs that belong to this pillar
  localCategoryPages?: string[]; // Optional local business category landing page URLs
  externalReferences: { title: string; url: string }[];
}

export type BlogConversionPath = {
  service?: PillarTopic;
  auditHref: "/free-ai-growth-audit/";
};

export const PILLAR_TOPICS: PillarTopic[] = [
  {
    id: "agentic-ai",
    name: "Gemini Enterprise Agentic AI for Marketing & Sales",
    slug: "/services/gemini-agentic-ai",
    description: "Building professional Gemini Enterprise Agentic AI environments that automate marketing collateral, sales enablement, market intelligence, target personas, and scalable GTM strategies.",
    primaryKeyword: "Gemini Enterprise Agentic AI marketing",
    secondaryKeywords: [
      "agentic AI for B2B",
      "AI marketing automation",
      "multi-agent AI GTM",
      "AI sales enablement",
      "marketing AI agents",
      "autonomous marketing workflows",
      "Gemini agent platform",
    ],
    relatedTags: ["Agentic AI", "AI Marketing Strategy"],
    spokeSlugs: [
      "openai-anthropic-new-model-clues-business-evaluation",
      "indiamart-agentic-ai-b2b-qualification-measurement",
      "ai-productivity-workflow-verification-costs",
      "grok-bot-costs-usage-limits-api-budgeting",
      "ai-api-budget-worksheet-estimate-reconcile",
      "nvidia-saudi-arabic-nemotron-35-asr-sada-dataset-2026",
      "is-zero-risk-ai-zero-value-consequence-budgets",
      "gemini-enterprise-agent-platform-replaces-vertex-ai",
      "multi-agent-ai-automate-gtm-2026",
      "bcg-cmo-survey-2026-agentic-marketing-transformation",
      "7-agentic-ai-workflows-replace-marketing-team",
      "gpt-5-6-openai-release-cadence-marketers",
      "claude-opus-4-8-vs-gpt-5-5-marketing-content",
      "spacex-cursor-acquisition-60b-ai-coding-agentic",
      "manus-pro-ai-agent-advanced-capabilities-vs-gemini-claude-2026",
      "google-agentic-resource-discovery-ard-hsbc-ai-partnership-2026",
      "five-eyes-ai-cybersecurity-warning-frontier-models-b2b-2026",
      "quantum-computing-trump-executive-orders-ai-banking-encryption-2026",
      "salesforce-agentforce-pay-per-resolution-enterprise-ai-pricing-2026",
      "zero-click-commerce-b2b-ai-agents-2026",
      "chatgpt-workspace-agents-b2b-gtm-dark-funnel-2026",
      "openai-gpt56-sol-ipo-b2b-marketing-strategy-2026",
      "ai-roi-paradox-b2b-marketing-measurement-2026",
      "ai-attribution-stack-chatgpt-perplexity-google-ai-mode-pipeline-2026",
      "agent-qualified-leads-aql-replace-mql-b2b-pipeline-2026",
      "claude-mythos-ai-cybersecurity-threat-banking-regulation-2026",
      "dora-ai-compliance-checklist-financial-services-2026",
      "meta-agentic-ai-slowdown-enterprise-transformation-risk-2026",
      "sp-global-ai-operating-model-b2b-intelligence-transformation-2026",
      "bank-of-england-agentic-ai-governance-circuit-breakers-financial-stability-2026",
      "openai-agentic-ai-delegated-tasks-b2b-productivity-enterprise-2026",
      "agent-washing-true-agentic-ai-vs-deterministic-workflows-enterprise-guide-2026",
      "chatgpt-work-agentic-marketing-workflow-2026",
      "gpt-5-6-cost-per-workflow-ai-metric-2026",
      "ai-agent-liability-identity-agentic-commerce-2026",
      "agentic-commerce-consent-gap-legal-framework-2026",
      "kimi-k3-open-vs-closed-ai-future",
      "kimi-k3-vs-gpt-5-6-claude-gemini",
      "kimi-k3-largest-open-ai-model",
      "enterprise-ai-model-roadmap-dependency-risk-2026",
      "forward-deployed-ai-engineers-replace-marketing-consultants-2026",
      "why-enterprise-ai-agents-fail-workflow-context-2026",
      "ai-digital-representative-brand-executive-2026",
      "agentic-ai-cost-overrun-workflow-economics-2026",
      "expedia-ai-disintermediation-agentic-commerce-2026",
      "agentic-data-foundation-enterprise-ai-readiness-2026",
      "ai-marketing-agency-credibility-test-2026",
      "ai-agent-contextual-authority-governance-security-2026",
      "openai-multi-agent-hugging-face-governance-2026",
      "salesforce-q2-fy27-agentforce-data-context-workflow-moat-2026",
      "openai-agi-2026-marketing-ai-discovery-strategy-guide",
      "gpt-6-astra-agentic-buyer-journey-b2b-marketing-2026",
      "ai-marketing-agent-reliable-task-closure-scorecard-2026",
      "gpt-6-astra-brand-voice-instruction-architecture-2026",
      "stop-rogue-ai-act-enterprise-agent-security-standards-2026",
      "rebid-agentic-media-control-plane-chatgpt-ads-2026",
      "agi-economic-threshold-marketing-work-governance-2026",
      "chatgpt-work-writing-style-connected-apps-governance-2026",
      "mistral-3bn-funding-sovereign-open-weight-ai-2026",
      "un-advanced-ai-human-rights-safeguards-business-2026",
      "openai-data-agent-chatgpt-work-marketing-measurement-governance-2026",
      "openai-glass-imaging-acquisition-agentic-perception-2026",
      "openai-sponsored-agents-chatgpt-ads-hubspot-shopify-2026",
      "google-business-agent-youtube-ads-ucp-2026-09-16",
      "openai-model-misalignment-reporting-framework-2026-09-16",
      "agentic-ai-content-workflow-global-brands",
      "voice-visual-search-agentic-commerce",
      "google-flow-ai-marketing-teams-aeo-2026",
      "organic-paid-agentic-ai-cpa-ltv",
      "us-china-ai-talks-superintelligence-marketing-manufacturing-jobs-2026",
      "zero-click-commerce-google-ai-mode-checkout-2026",
      "ai-agent-landing-page-conversion-layer-2026",
      "human-in-the-loop-wrong-way-to-govern-ai-agents-2026",
      "buyer-agent-merchant-agent-ecommerce-2026",
      "will-ai-kill-saas-or-the-interface-2026",
      "openai-gpt-6-1-astra-safety-governance-2026",
      "openai-70bn-revenue-run-rate-ipo-economics-2026",
      "instinct-personal-agent-trust-alignment-2026",
      "cue-by-manus-personal-agent-team-governance-2026",
      "america-gov-phase-1-launch-gemini-and-phase-2",
      "what-is-superintelligence-ai-agi-asi-marketing-2026",
      "gemini-4-argon-frontier-model-cybersecurity-governance-2026",
      "governed-ai-lead-generation-workflow-human-approval-2026",
      "chatgpt-virtual-try-on-favorites-ai-consideration",
      "ftc-openai-anthropic-agent-accountability",
      "retailer-agent-access-policy-ai-shopping-2026",
      "ai-agents-consumer-inertia-margin-resilience-2026",
      "ai-agent-operational-identity-delegation-2026",
      "maximum-autonomous-consequence-ai-agent-governance-2026",
      "ai-agent-employee-identity-access-revocation-2026",
      "ai-marketing-persona-paid-and-earned",
      "uk-ai-marketing-agency-who-does-the-work-who-signs",
      "agentic-traffic-demand-visibility-agent-experience-2026",
      "enterprise-ai-agents-knowledge-context-production-2026",
      "chatgpt-ads-vs-agentic-demand-attribution-2026",
      "openai-openads-optimizer-testflight-warning-2026",
      "securing-testflight-oauth-ai-app-impersonation-2026",
      "orbital-ai-data-centres-latency-water-safety-2026",

      "sabi-brain-ai-cap-neural-data-consent-governance-2026",
      "visa-bill-billtrust-agentic-b2b-payments-pilot-2026",
      "google-gemini-small-business-marketing-tools-2026",

      "ai-content-provenance-marketing-claim-ledger-2026",
      "ai-marketing-model-routing-acceptance-criteria-2026",
      "ai-marketing-evaluation-sets-content-routing-2026",

    ],
    externalReferences: [
      { title: "Google Cloud Agent Builder", url: "https://cloud.google.com/products/agent-builder" },
      { title: "Gartner: Agentic AI", url: "https://www.gartner.com/en/topics/agentic-ai" },
      { title: "McKinsey: AI Agents in Enterprise", url: "https://www.mckinsey.com/capabilities/mckinsey-digital/our-insights" },
    ],
  },
  {
    id: "aeo-geo",
    name: "AI Answer Engine Optimization (AEO) & Generative Engine Optimization (GEO)",
    slug: "/services/seo-aeo-geo",
    description: "Getting cited in AI answers, Google AI Overviews, AI Mode, ChatGPT, Gemini, and Perplexity, through answer-first content modules, entity authority, and structured schema.",
    primaryKeyword: "answer engine optimization AEO",
    secondaryKeywords: [
      "generative engine optimization GEO",
      "AI Overviews optimization",
      "AI Mode SEO",
      "ChatGPT citation strategy",
      "LLM visibility",
      "AI search optimization",
      "share of AI citations",
    ],
    relatedTags: ["AEO & AI Search", "AI Marketing Strategy"],
    spokeSlugs: [
      "ai-search-follow-up-questions-content-ownership",
      "ai-answer-verification-website-evidence",
      "gpt6-intelligent-ui-generative-experience-2026",
      "radisson-chatgpt-decision-ready-demand-2026",
      "google-ai-shopping-agents-decision-friction-2026",
      "ai-seo-evidence-gate-agentic-diagnosis-2026",
      "is-universal-geo-strategy-dead-engine-specific-authority",
      "aeo-2026-complete-guide-answer-engine-optimisation-b2b",
      "ai-search-optimization-2026-seo-geo",
      "11-top-ai-answers-seo-services-b2b-fintech",
      "conversational-commerce-ai-search-2026",
      "nano-banana-google-search-button",
      "google-flow-ai-marketing-teams-aeo-2026",
      "voice-search-apple-qai-acquisition",
      "zero-click-searches-how-to-win",
      "track-ai-overview-impressions-google-search-console",
      "ai-visibility-benchmark-2026-citation-rates-share-of-voice",
      "write-content-cited-chatgpt-answers",
      "ai-search-ads-inside-the-answer-b2b-marketers-q3-2026",
      "chatgpt-workspace-agents-b2b-gtm-dark-funnel-2026",
      "google-ai-mode-vs-ai-overviews-b2b-marketers-2026",
      "preferred-source-chatgpt-gemini-perplexity",
      "ai-attribution-stack-chatgpt-perplexity-google-ai-mode-pipeline-2026",
      "ai-visibility-reset-b2b-tech-marketers-ai-search-2026",
      "google-search-console-ai-reports-attribution-blind-spot-b2b-2026",
      "dark-ai-phase-b2b-aeo-roi-measurement-2026",
      "b2b-ai-search-visibility-gap-ai-overviews-2026",
      "b2b-ai-citation-rate-measurement-guide-2026",
      "chatgpt-ads-b2b-marketing-strategy-openai-cannes-2026",
      "how-to-migrate-b2b-website-to-ai-website-checklist-london",
      "gpt-live-openai-voice-model-b2b-marketing-2026",
      "ai-website-vs-wordpress-b2b-2026",
      "what-does-an-aeo-agency-uk-do-b2b-buyers-guide-2026",
      "aeo-agency-what-to-hire-for-2026",
      "how-to-choose-ai-search-aeo-agency-b2b-2026",
      "aeo-agency-pricing-uk-cost-roi-guide-2026",
      "how-to-measure-aeo-results-b2b-analytics-guide-2026",
      "aeo-agency-vs-in-house-vs-hybrid-b2b-decision-guide-2026",
      "/services/aeo-agency-london/",
      "/services/geo-agency-london/",
      "/services/ai-marketing-agency-london/",
      "microsoft-clarity-topic-insights-ai-search-content-2026",
      "ai-search-vendor-research-budget-shift-fractl-survey-2026",
      "ai-b2b-buyer-verification-layer-vendor-transparency-2026",
      "creator-first-aeo-creator-corroboration-ai-search-2026",
      "brightedge-agent-edge-second-web-ai-agent-visitors-2026",
      "openai-agi-2026-marketing-ai-discovery-strategy-guide",
      "gemini-3-8-flash-google-ai-mode-geo-visibility-2026",
      "gpt-6-astra-agentic-buyer-journey-b2b-marketing-2026",
      "gpt-6-astra-brand-voice-instruction-architecture-2026",
      "google-ai-search-console-global-reporting-measurement-2026",
      "openai-glass-imaging-acquisition-agentic-perception-2026",
      "google-merchant-center-ai-performance-insights-share-of-voice-2026",
      "google-business-agent-youtube-ads-ucp-2026-09-16",
      "zen-media-b2b-saas-ai-visibility-documentation-2026-09-16",
      "transparent-aeo-evidence-based-ai-visibility-scoring-2026",
      "technical-seo-ai-search-search-console",
      "voice-visual-search-agentic-commerce",
      "zero-click-commerce-google-ai-mode-checkout-2026",
      "will-ai-kill-saas-or-the-interface-2026",
      "aeo-agency-what-to-hire-for-2026",
      "america-gov-phase-1-launch-gemini-and-phase-2",
      "uk-b2b-ai-marketing-one-persona-plan-paid-search-seo-2026",
      "best-b2b-agencies-london-by-published-fee",
      "saas-brand-visibility-ai-answers-90-day-practical-stages",
      "seo-ai-driven-search-one-useful-definition-2026",
      "llmo-agency-uk-what-it-costs",
      "ai-answers-agency-uk-medium-business",
      "ai-marketing-persona-paid-and-earned",
      "ai-assisted-content-production-saas-quality-standard",
      "saas-aeo-case-study-what-it-must-show",
      "ai-visibility-ga4-search-console-crm-not-full-attribution",
      "google-ai-mode-monitoring-latent-agentic-demand",
      "chatgpt-virtual-try-on-favorites-ai-consideration",
      "retailer-agent-access-policy-ai-shopping-2026",
      "what-uk-b2b-companies-should-buy-from-an-ai-marketing-agency",
      "first-90-days-uk-aeo-programme-what-you-get",
      "ai-visibility-check-vs-vanity-score-uk-b2b",
      "is-aeo-a-new-channel-or-one-seo-job",
      "organic-rank-is-not-ai-citation",
      "agentic-traffic-demand-visibility-agent-experience-2026",
      "chatgpt-ads-vs-agentic-demand-attribution-2026",
      "how-to-check-chatgpt-recommends-your-brand",
      "aeo-citation-panel-operating-sheet-b2b-2026",
      "demandbase-b2a-aeo-buying-committees-2026",

      "chatgpt-search-20-percent-google-query-volume",
    ],
    externalReferences: [
      { title: "Google Search Central: AI Overviews", url: "https://developers.google.com/search/docs/appearance/ai-overviews" },
      { title: "Schema.org Structured Data", url: "https://schema.org/" },
      { title: "Moz: AEO Guide", url: "https://moz.com/blog" },
    ],
  },
  {
    id: "ppc-performance",
    name: "PPC & Performance Max (ROAS-Led Google Ads)",
    slug: "/services/ppc-performance-max",
    description: "Auditing, rebuilding, and scaling Google Ads, Shopping, and Performance Max campaigns with persona-driven targeting, structured A/B testing, and conversion tracking discipline.",
    primaryKeyword: "PPC Performance Max agency",
    secondaryKeywords: [
      "Google Ads management",
      "ROAS optimization",
      "Performance Max strategy",
      "A/B testing PPC",
      "conversion rate optimization",
      "persona-driven PPC",
      "Google Shopping ads",
    ],
    relatedTags: ["PPC & Performance Max", "AI Marketing Strategy"],
    spokeSlugs: [
      "chatgpt-ads-google-search-revenue-study-test-decision",
      "chatgpt-ai-ads-coming",
      "conversational-commerce-ai-search-2026",
      "ai-search-ads-inside-the-answer-b2b-marketers-q3-2026",
      "pmax-vs-smart-campaigns-b2b-2026",
      "openai-cannes-2026-chatgpt-ads-b2b-commercial-intent",
      "meta-q1-2026-earnings-b2b-marketing-intelligence",
      "chatgpt-ads-uk-b2b-decision-framework-2026",
      "retarget-framework-b2b-ai-marketing-methodology",
      "google-ai-mode-business-agent-leads-b2b-2026",
      "google-ads-dv360-brand-errors-ai-label-compliance-2026",
      "google-ai-business-agent-search-ads-lead-forms-2026",
      "chatgpt-ads-automated-bidding-view-through-attribution-2026",
      "should-you-bid-on-your-own-brand-brand-ppc-incrementality-2026",
      "rebid-agentic-media-control-plane-chatgpt-ads-2026",
      "sebamed-wpp-chatgpt-advertising-measurement-2026",
      "chatgpt-ads-emea-ads-manager-measurement-2026",
      "openai-sponsored-agents-chatgpt-ads-hubspot-shopify-2026",
      "google-business-agent-youtube-ads-ucp-2026-09-16",
      "organic-paid-agentic-ai-cpa-ltv",
      "zero-click-commerce-google-ai-mode-checkout-2026",
      "ai-agent-landing-page-conversion-layer-2026",
      "uk-b2b-ai-marketing-one-persona-plan-paid-search-seo-2026",
      "b2b-paid-social-agency-what-you-are-hiring",
      "b2b-ai-workflows-campaign-roi-what-to-ask",
      "what-uk-b2b-companies-should-buy-from-an-ai-marketing-agency",
      "chatgpt-ads-visual-format-measurement-reasoned-intent-2026",
      "chatgpt-ads-vs-agentic-demand-attribution-2026",
      "openai-openads-optimizer-testflight-warning-2026",
      "securing-testflight-oauth-ai-app-impersonation-2026",
    ],
    externalReferences: [
      { title: "Google Ads Help Center", url: "https://support.google.com/google-ads" },
      { title: "Google Performance Max Best Practices", url: "https://support.google.com/google-ads/answer/10724817" },
    ],
  },
  {
    id: "ai-news-trends",
    name: "AI Breaking News, Trends & Market Intelligence",
    slug: "/blog",
    description: "Daily coverage of AI breaking news, model releases, startup funding, and market shifts, with Modi Elnadi's POV on what it means for marketing leaders and business transformation.",
    primaryKeyword: "AI news trends 2026",
    secondaryKeywords: [
      "AI breaking news",
      "AI startup funding",
      "AI market trends",
      "AI model releases",
      "AI industry analysis",
      "Nasdaq AI stocks",
    ],
    relatedTags: ["AI News & Trends"],
    spokeSlugs: [
      "openai-anthropic-new-model-clues-business-evaluation",
      "indiamart-agentic-ai-b2b-qualification-measurement",
      "ai-answer-verification-website-evidence",
      "chatgpt-ads-google-search-revenue-study-test-decision",
      "grok-bot-costs-usage-limits-api-budgeting",
      "gpt6-intelligent-ui-generative-experience-2026",
      "radisson-chatgpt-decision-ready-demand-2026",
      "google-ai-shopping-agents-decision-friction-2026",
      "ai-seo-evidence-gate-agentic-diagnosis-2026",
      "nvidia-saudi-arabic-nemotron-35-asr-sada-dataset-2026",
      "moltbook-ai-only-social-network-explained",
      "voice-search-apple-qai-acquisition",
      "nano-banana-google-search-button",
      "ai-replacing-jobs-what-you-need-to-know",
      "top-10-ai-startups-uk-2026",
      "openai-ipo-hurdles-vs-anthropic",
      "anthropic-ipo-s1-claude-compute-economics-2026",
      "spacex-cursor-acquisition-60b-ai-coding-agentic",
      "ai-stocks-cerebras-anthropic-spacex-agentic-future",
      "spacex-orbital-data-centers-ai-marketing-infrastructure",
      "spacex-amazon-leo-spectrum-hybrid-network-2026",
      "google-agentic-resource-discovery-ard-hsbc-ai-partnership-2026",
      "five-eyes-ai-cybersecurity-warning-frontier-models-b2b-2026",
      "quantum-computing-trump-executive-orders-ai-banking-encryption-2026",
      "ai-adoption-divide-gallup-layoff-risk-marketing-teams-2026",
      "your-competitors-ai-agents-are-already-running-without-you",
      "salesforce-agentforce-pay-per-resolution-enterprise-ai-pricing-2026",
      "zero-click-commerce-b2b-ai-agents-2026",
      "ai-capex-2026-b2b-marketing-budget-implications",
      "openai-cannes-2026-chatgpt-ads-b2b-commercial-intent",
      "openai-gpt56-sol-ipo-b2b-marketing-strategy-2026",
      "meta-q1-2026-earnings-b2b-marketing-intelligence",
      "google-ai-mode-business-agent-leads-b2b-2026",
      "people-trust-ai-more-than-friends-survey-2026",
      "ai-discoverability-pipeline-problem-b2b-revenue-2026",
      "ai-investment-middle-east-humain-pif-g42-mgx-2026",
      "kuwait-helix-ai-infrastructure-kia-kkr-nvidia-vistra-2026",
      "qatar-qai-sovereign-ai-company-qia-brookfield-20-billion-2026",
      "gulf-sovereign-ai-series-humain-g42-helix-qai-2026",
      "humain-nvidia-84mw-ai-compute-plan-saudi-arabia-2026",
      "saudi-ai-giga-campus-humain-nebius-datavalt-compute-economics-2026",
      "uae-g42-mgx-abu-dhabi-sovereign-ai-empire-2026",
      "gpt-5-6-ai-search-digital-marketing-impact",
      "google-ai-search-training-data-customer-behaviour-privacy-2026",
      "bank-of-england-ai-governance-circuit-breaker-agentic-systems-2026",
      "gpt-live-openai-voice-model-b2b-marketing-2026",
      "chatgpt-work-agentic-marketing-workflow-2026",
      "gpt-5-6-cost-per-workflow-ai-metric-2026",
      "ai-agent-liability-identity-agentic-commerce-2026",
      "google-ai-ad-labels-consumer-trust-governance-2026",
      "enterprise-ai-measurement-usage-vs-outcomes-2026",
      "forward-deployed-ai-engineers-replace-marketing-consultants-2026",
      "why-enterprise-ai-agents-fail-workflow-context-2026",
      "google-ads-dv360-brand-errors-ai-label-compliance-2026",
      "ai-digital-representative-brand-executive-2026",
      "chatgpt-gpt-56-sol-terra-luna-complete-guide-2026",
      "linkedin-360brew-algorithm-b2b-marketing-guide-2026",
      "expedia-ai-disintermediation-agentic-commerce-2026",
      "agentic-data-foundation-enterprise-ai-readiness-2026",
      "ai-marketing-agency-credibility-test-2026",
      "microsoft-clarity-topic-insights-ai-search-content-2026",
      "ai-search-vendor-research-budget-shift-fractl-survey-2026",
      "chatgpt-ads-automated-bidding-view-through-attribution-2026",
      "ai-b2b-buyer-verification-layer-vendor-transparency-2026",
      "ai-agent-contextual-authority-governance-security-2026",
      "openai-multi-agent-hugging-face-governance-2026",
      "creator-first-aeo-creator-corroboration-ai-search-2026",
      "salesforce-q2-fy27-agentforce-data-context-workflow-moat-2026",
      "brightedge-agent-edge-second-web-ai-agent-visitors-2026",
      "gemini-3-8-flash-google-ai-mode-geo-visibility-2026",
      "openai-automated-shutdown-agent-control-plane-2026",
      "gpt-6-astra-agentic-buyer-journey-b2b-marketing-2026",
      "ai-marketing-agent-reliable-task-closure-scorecard-2026",
      "gpt-6-astra-brand-voice-instruction-architecture-2026",
      "stop-rogue-ai-act-enterprise-agent-security-standards-2026",
      "rebid-agentic-media-control-plane-chatgpt-ads-2026",
      "agi-economic-threshold-marketing-work-governance-2026",
      "chatgpt-work-writing-style-connected-apps-governance-2026",
      "sebamed-wpp-chatgpt-advertising-measurement-2026",
      "mistral-3bn-funding-sovereign-open-weight-ai-2026",
      "un-advanced-ai-human-rights-safeguards-business-2026",
      "humain-saudi-ai-infrastructure-ipo-preparation-2026",
      "google-ai-search-console-global-reporting-measurement-2026",
      "chatgpt-ads-emea-ads-manager-measurement-2026",
      "openai-data-agent-chatgpt-work-marketing-measurement-governance-2026",
      "openai-sponsored-agents-chatgpt-ads-hubspot-shopify-2026",
      "google-merchant-center-ai-performance-insights-share-of-voice-2026",
      "google-business-agent-youtube-ads-ucp-2026-09-16",
      "openai-model-misalignment-reporting-framework-2026-09-16",
      "zen-media-b2b-saas-ai-visibility-documentation-2026-09-16",
      "us-china-ai-talks-superintelligence-marketing-manufacturing-jobs-2026",
      "openai-gpt-6-1-astra-safety-governance-2026",
      "openai-70bn-revenue-run-rate-ipo-economics-2026",
      "instinct-personal-agent-trust-alignment-2026",
      "cue-by-manus-personal-agent-team-governance-2026",
      "america-gov-phase-1-launch-gemini-and-phase-2",
      "what-is-superintelligence-ai-agi-asi-marketing-2026",
      "gemini-4-argon-frontier-model-cybersecurity-governance-2026",
      "retailer-agent-access-policy-ai-shopping-2026",
      "ai-agents-consumer-inertia-margin-resilience-2026",
      "ai-agent-operational-identity-delegation-2026",
      "maximum-autonomous-consequence-ai-agent-governance-2026",
      "ai-agent-employee-identity-access-revocation-2026",
      "chatgpt-ads-visual-format-measurement-reasoned-intent-2026",
      "agentic-traffic-demand-visibility-agent-experience-2026",
      "enterprise-ai-agents-knowledge-context-production-2026",
      "chatgpt-ads-vs-agentic-demand-attribution-2026",
      "openai-openads-optimizer-testflight-warning-2026",
      "securing-testflight-oauth-ai-app-impersonation-2026",
      "orbital-ai-data-centres-latency-water-safety-2026",
      "spacex-nvidia-ai-infrastructure-financing-2026",
      "ast-spacemobile-bluebird-satellite-analyst-rating-2026",
      "aurora-autonomous-trucking-fmcsa-warning-beacon-exemption-2026",
      "nvidia-open-ai-factory-standards-ocp-2026",
      "gemini-4-argon-ai-mode-comparative-evidence-2026",
      "google-tag-manager-optimised-containers-measurement-2026",
      "openai-recording-device-security-case-2026",
      "tesla-flying-roadster-spacex-merger-2026",
      "ios-272-beta-4-siri-ai-features-2026",

      "sabi-brain-ai-cap-neural-data-consent-governance-2026",
      "demandbase-b2a-aeo-buying-committees-2026",
      "visa-bill-billtrust-agentic-b2b-payments-pilot-2026",
      "google-gemini-small-business-marketing-tools-2026",

    ],
    externalReferences: [
      { title: "TechCrunch AI", url: "https://techcrunch.com/category/artificial-intelligence/" },
      { title: "The Information", url: "https://www.theinformation.com/" },
      { title: "Bloomberg Technology", url: "https://www.bloomberg.com/technology" },
    ],
  },
  {
    id: "ai-governance",
    name: "AI Governance, Safety & Regulatory Compliance for B2B",
    slug: "/services/ai-governance",
    description: "Navigating AI governance frameworks, frontier model safety, regulatory compliance, and responsible AI deployment for B2B marketing and enterprise operations, with practical guidance on risk management, audit trails, and policy alignment.",
    primaryKeyword: "AI governance B2B compliance",
    secondaryKeywords: [
      "AI safety regulations",
      "responsible AI marketing",
      "AI risk management",
      "frontier model governance",
      "AI compliance framework",
      "agentic AI guardrails",
      "AI audit trail",
    ],
    relatedTags: ["AI Governance & Safety", "Agentic AI"],
    spokeSlugs: [
      "ai-productivity-workflow-verification-costs",
      "is-zero-risk-ai-zero-value-consequence-budgets",
      "ai-governance-2026-quality-control",
      "five-eyes-ai-cybersecurity-warning-frontier-models-b2b-2026",
      "quantum-computing-trump-executive-orders-ai-banking-encryption-2026",
      "your-competitors-ai-agents-are-already-running-without-you",
      "ai-adoption-divide-gallup-layoff-risk-marketing-teams-2026",
      "claude-mythos-ai-cybersecurity-threat-banking-regulation-2026",
      "dora-ai-compliance-checklist-financial-services-2026",
      "ai-agent-liability-identity-agentic-commerce-2026",
      "google-ai-ad-labels-consumer-trust-governance-2026",
      "enterprise-ai-measurement-usage-vs-outcomes-2026",
      "why-enterprise-ai-agents-fail-workflow-context-2026",
      "google-ads-dv360-brand-errors-ai-label-compliance-2026",
      "agentic-commerce-consent-gap-legal-framework-2026",
      "kimi-k3-open-vs-closed-ai-future",
      "kimi-k3-vs-gpt-5-6-claude-gemini",
      "kimi-k3-largest-open-ai-model",
      "enterprise-ai-model-roadmap-dependency-risk-2026",
      "gpt-5-6-cyber-trusted-capability-ai-governance-2026",
      "ai-agent-contextual-authority-governance-security-2026",
      "openai-multi-agent-hugging-face-governance-2026",
      "openai-agi-2026-marketing-ai-discovery-strategy-guide",
      "openai-automated-shutdown-agent-control-plane-2026",
      "gpt-6-astra-agentic-buyer-journey-b2b-marketing-2026",
      "ai-marketing-agent-reliable-task-closure-scorecard-2026",
      "stop-rogue-ai-act-enterprise-agent-security-standards-2026",
      "mistral-3bn-funding-sovereign-open-weight-ai-2026",
      "un-advanced-ai-human-rights-safeguards-business-2026",
      "humain-saudi-ai-infrastructure-ipo-preparation-2026",
      "openai-model-misalignment-reporting-framework-2026-09-16",
      "agentic-ai-content-workflow-global-brands",
      "human-in-the-loop-ai-content-governance",
      "organic-paid-agentic-ai-cpa-ltv",
      "us-china-ai-talks-superintelligence-marketing-manufacturing-jobs-2026",
      "human-in-the-loop-wrong-way-to-govern-ai-agents-2026",
      "abm-for-fintech-and-financial-services",
      "openai-gpt-6-1-astra-safety-governance-2026",
      "openai-70bn-revenue-run-rate-ipo-economics-2026",
      "instinct-personal-agent-trust-alignment-2026",
      "cue-by-manus-personal-agent-team-governance-2026",
      "america-gov-phase-1-launch-gemini-and-phase-2",
      "what-is-superintelligence-ai-agi-asi-marketing-2026",
      "gemini-4-argon-frontier-model-cybersecurity-governance-2026",
      "governed-ai-lead-generation-workflow-human-approval-2026",
      "ai-agent-operational-identity-delegation-2026",
      "maximum-autonomous-consequence-ai-agent-governance-2026",
      "ai-agent-employee-identity-access-revocation-2026",
      "enterprise-ai-agents-knowledge-context-production-2026",
      "openai-openads-optimizer-testflight-warning-2026",
      "securing-testflight-oauth-ai-app-impersonation-2026",
      "ai-seo-evidence-gate-agentic-diagnosis-2026",
      "orbital-ai-data-centres-latency-water-safety-2026",

      "sabi-brain-ai-cap-neural-data-consent-governance-2026",
      "ai-marketing-thin-evidence-human-review",
      "ai-marketing-brief-before-automation-decision-contract-2026",
    ],
    externalReferences: [
      { title: "NIST AI Risk Management Framework", url: "https://www.nist.gov/artificial-intelligence" },
      { title: "EU AI Act Overview", url: "https://digital-strategy.ec.europa.eu/en/policies/regulatory-framework-ai" },
      { title: "UK AI Safety Institute", url: "https://www.gov.uk/government/organisations/ai-safety-institute" },
    ],
  },
  {
    id: "account-based-marketing",
    name: "Account-Based Marketing & AI-Powered ABM",
    slug: "/services/account-based-marketing",
    description: "AI-powered ABM frameworks, buying committee intelligence, and predictive account scoring for enterprise B2B teams that need to drive pipeline from the right accounts.",
    primaryKeyword: "AI-powered ABM enterprise B2B 2026",
    secondaryKeywords: [
      "account-based marketing strategy",
      "AI ABM programme",
      "buying committee mapping",
      "predictive account scoring",
      "B2B demand generation AI",
      "ABM personalisation at scale",
    ],
    relatedTags: ["Account-Based Marketing", "AI Marketing Strategy", "B2B Marketing"],
    spokeSlugs: [
      "ai-powered-abm-enterprise-b2b-2026",
      "multi-agent-ai-automate-gtm-2026",
      "chatgpt-workspace-agents-b2b-gtm-dark-funnel-2026",
      "zero-click-commerce-b2b-ai-agents-2026",
      "bcg-cmo-survey-2026-agentic-marketing-transformation",
      "ai-agents-replacing-b2b-research-phase",
      "abm-agency-london-what-good-looks-like",
      "abm-for-fintech-and-financial-services",
      "uk-b2b-ai-marketing-one-persona-plan-paid-search-seo-2026",
      "demandbase-b2a-aeo-buying-committees-2026",

    ],
    externalReferences: [
      { title: "Demandbase ABM Research", url: "https://www.demandbase.com/blog/ai-in-account-based-marketing/" },
      { title: "2026 ABM Benchmark Survey", url: "https://www.demandgenreport.com/" },
      { title: "6sense ABM Platform", url: "https://6sense.com/" },
    ],
  },
  {
    id: "integrated-marketing-communications",
    name: "Integrated Marketing Communications in the AI Era",
    slug: "/services/integrated-marketing-communications/",
    description: "The five-capability IMC framework for B2B brands that need SEO, AEO, GEO, PPC, and agentic AI under one attribution model, delivered by London's integrated marketing communications agency.",
    primaryKeyword: "integrated marketing communications agency London",
    secondaryKeywords: [
      "integrated marketing communications agencies AI SEO services",
      "IMC agency B2B",
      "integrated digital marketing agency UK",
      "unified marketing attribution B2B",
      "full service AI marketing agency London",
    ],
    relatedTags: ["AI Marketing Strategy", "AEO & AI Search", "Digital Marketing Tips"],
    spokeSlugs: [
      "integrated-marketing-communications-agency-ai-seo-aeo-london-2026",
      "top-10-integrated-marketing-communications-agencies-london-2026",
      "uk-b2b-ai-marketing-one-persona-plan-paid-search-seo-2026",
      "integrated-marketing-b2b-tech-ai-visibility-lead-generation",
    ],
    externalReferences: [
      { title: "Don Schultz IMC Framework - Northwestern Medill", url: "https://www.medill.northwestern.edu/imc/" },
      { title: "Marketing Week - Integrated Marketing", url: "https://www.marketingweek.com/topic/integrated-marketing/" },
      { title: "B2B Marketing - Agency Benchmarking", url: "https://www.b2bmarketing.net/" },
    ],
  },
  {
    id: "digital-marketing-tips",
    name: "Digital Marketing Tips & AI Marketing Playbooks",
    slug: "/blog",
    description: "Actionable tips, frameworks, and playbooks for B2B and B2C marketers who want to integrate AI into their existing marketing operations without losing brand authenticity.",
    primaryKeyword: "AI marketing tips 2026",
    secondaryKeywords: [
      "digital marketing automation",
      "B2B marketing strategy",
      "content marketing AI",
      "marketing playbook",
      "lead generation tips",
      "brand positioning AI",
    ],
    relatedTags: ["Digital Marketing Tips", "AI Marketing Strategy"],
    spokeSlugs: [
      "ai-search-follow-up-questions-content-ownership",
      "automation-vs-human-judgement-ai-marketing",
      "hidden-cost-of-producing-more-content-ai",
      "7-agentic-ai-workflows-replace-marketing-team",
      "multi-agent-ai-automate-gtm-2026",
      "bcg-cmo-survey-2026-agentic-marketing-transformation",
      "aeo-2026-complete-guide-answer-engine-optimisation-b2b",
      "ai-governance-2026-quality-control",
      "track-ai-overview-impressions-google-search-console",
      "write-content-cited-chatgpt-answers",
      "zero-click-searches-how-to-win",
      "ai-adoption-divide-gallup-layoff-risk-marketing-teams-2026",
      "your-competitors-ai-agents-are-already-running-without-you",
      "zero-click-commerce-b2b-ai-agents-2026",
      "ai-capex-2026-b2b-marketing-budget-implications",
      "ai-roi-paradox-b2b-marketing-measurement-2026",
      "ai-attribution-stack-chatgpt-perplexity-google-ai-mode-pipeline-2026",
      "ai-visibility-reset-b2b-tech-marketers-ai-search-2026",
      "google-search-console-ai-reports-attribution-blind-spot-b2b-2026",
      "dark-ai-phase-b2b-aeo-roi-measurement-2026",
      "retarget-framework-b2b-ai-marketing-methodology",
      "how-to-migrate-b2b-website-to-ai-website-checklist-london",
      "creator-first-aeo-creator-corroboration-ai-search-2026",
      "salesforce-q2-fy27-agentforce-data-context-workflow-moat-2026",
      "brightedge-agent-edge-second-web-ai-agent-visitors-2026",
      "openai-agi-2026-marketing-ai-discovery-strategy-guide",
      "refresh-repurpose-or-create-new-content-decision-guide-2026",
      "google-gemini-small-business-marketing-tools-2026",
      "ai-marketing-brief-before-automation-decision-contract-2026",

      "ai-content-provenance-marketing-claim-ledger-2026",
      "ai-marketing-model-routing-acceptance-criteria-2026",
      "ai-marketing-evaluation-sets-content-routing-2026",

    ],
    externalReferences: [
      { title: "HubSpot Marketing Blog", url: "https://blog.hubspot.com/marketing" },
      { title: "Content Marketing Institute", url: "https://contentmarketinginstitute.com/" },
    ],
  },
  {
    id: "local-sme",
    name: "AI Marketing for Local Businesses",
    slug: "/services/local-presence-plan/",
    description: "AI marketing plans for local businesses in SW London, Surrey, Richmond, Putney, Wimbledon, Kingston, Guildford, UK, USA, EMEA, and MENA.",
    primaryKeyword: "AI marketing for local businesses near me",
    secondaryKeywords: [
      "local business marketing Richmond",
      "Google Maps SEO Wimbledon",
      "near me SEO for cafes gyms restaurants",
      "local presence plan SW London",
      "AI website for small business Surrey",
      "digital marketing Guildford",
      "Arabic marketing agency London",
    ],
    relatedTags: ["Local SEO", "Google Business Profile", "SME Marketing", "Near Me SEO", "LocalBusiness Schema"],
    spokeSlugs: [
      "ai-marketing-agency-credibility-test-2026",
      "microsoft-clarity-topic-insights-ai-search-content-2026",
    ],
    localCategoryPages: [
      "/local/cafe-marketing-richmond-putney-wimbledon/",
      "/local/gym-marketing-putney-wimbledon-kingston/",
      "/local/pilates-studio-marketing-sw-london-surrey/",
      "/local/restaurant-marketing-richmond-kingston-guildford/",
      "/local/salon-beauty-marketing-sw-london-surrey/",
      "/local/clinic-medical-dental-marketing-sw-london/",
      "/local/law-firm-solicitor-marketing-sw-london-surrey/",
      "/local/tanning-salon-marketing-sw-london-surrey/",
      "/local/tyre-mot-garage-marketing-sw-london-surrey/",
    ],
    externalReferences: [
      { title: "Google Business Profile Help", url: "https://support.google.com/business/" },
      { title: "Schema.org LocalBusiness", url: "https://schema.org/LocalBusiness" },
      { title: "Google Maps Platform", url: "https://developers.google.com/maps" },
    ],
  },
];

/**
 * Get the pillar topic(s) a blog post belongs to based on its slug
 */
export function getPillarsForPost(slug: string): PillarTopic[] {
  return PILLAR_TOPICS.filter((p) => p.spokeSlugs.includes(slug));
}

/**
 * Get sibling spokes (posts in the same pillar cluster) for cross-linking
 */
export function getSiblingSpokes(slug: string): string[] {
  const pillars = getPillarsForPost(slug);
  const siblings = new Set<string>();
  pillars.forEach((p) => {
    p.spokeSlugs.forEach((s) => {
      if (s !== slug) siblings.add(s);
    });
  });
  return Array.from(siblings);
}

/**
 * Get external references for a post based on its pillar membership
 */
export function getExternalReferences(slug: string): { title: string; url: string }[] {
  const pillars = getPillarsForPost(slug);
  const refs: { title: string; url: string }[] = [];
  pillars.forEach((p) => {
    p.externalReferences.forEach((r) => {
      if (!refs.some((existing) => existing.url === r.url)) {
        refs.push(r);
      }
    });
  });
  return refs;
}

/**
 * Get the primary keyword for a post's pillar (for breadcrumb and internal linking)
 */
export function getPillarKeywords(slug: string): string[] {
  const pillars = getPillarsForPost(slug);
  return pillars.map((p) => p.primaryKeyword);
}

/**
 * Returns one commercially relevant next step for an editorial post without
 * requiring authors to maintain per-post CTA fields. Pillar membership wins;
 * the canonical tag fallback is deliberately narrow to avoid irrelevant links.
 */
// Only use a tag fallback where the canonical topic has a matching, valid
// service pillar. Other topics retain their archive-level next step without
// forcing an editorial article into an unrelated commercial conversion path.
const TAG_TO_SERVICE_PILLAR: Partial<Record<string, string>> = {
  "Agentic AI": "agentic-ai",
  "AEO & AI Search": "aeo-geo",
  "AI Governance & Safety": "ai-governance",
  "PPC & Performance Max": "ppc-performance",
};

export function getBlogConversionPath(slug: string, tags: string[]): BlogConversionPath {
  const serviceFromPillar = getPillarsForPost(slug).find((pillar) => pillar.slug.startsWith("/services/"));
  if (serviceFromPillar) return { service: serviceFromPillar, auditHref: "/free-ai-growth-audit/" };

  const fallbackPillarId = tags.map((tag) => TAG_TO_SERVICE_PILLAR[tag]).find(Boolean);
  const fallbackService = fallbackPillarId
    ? PILLAR_TOPICS.find((pillar) => pillar.id === fallbackPillarId && pillar.slug.startsWith("/services/"))
    : undefined;

  return { service: fallbackService, auditHref: "/free-ai-growth-audit/" };
}

/**
 * Returns a small, deterministic set of directly relevant service pillars.
 * Direct pillar membership comes first; tag fallbacks are only used when a
 * post has fewer matches. This avoids generic or editorially irrelevant CTAs.
 */
export function getRelatedServicesForPost(slug: string, tags: string[], limit = 3): PillarTopic[] {
  const directServices = getPillarsForPost(slug).filter((pillar) => pillar.slug.startsWith("/services/"));
  const fallbackServices = tags
    .map((tag) => TAG_TO_SERVICE_PILLAR[tag])
    .filter((pillarId): pillarId is string => Boolean(pillarId))
    .map((pillarId) => PILLAR_TOPICS.find((pillar) => pillar.id === pillarId && pillar.slug.startsWith("/services/")))
    .filter((pillar): pillar is PillarTopic => Boolean(pillar));

  return [...directServices, ...fallbackServices]
    .filter((service, index, all) => all.findIndex((candidate) => candidate.id === service.id) === index)
    .slice(0, limit);
}
